import React, { useEffect, useState } from "react";

/**
 * LiveAmbience: makes a hero feel alive without video.
 *
 * Three layers:
 *  1. Floating "ember" particles (gold dust drifting upward, very subtle)
 *  2. A "scan-line" — a vertical sweep that grazes the section every ~7s
 *  3. A live "departure board" ticker strip running along the bottom edge
 *     (sample timetable strings; purely decorative — feels live)
 *
 * Designed to sit `absolute inset-0` inside a relative hero section, after
 * the photo + gradient overlay, before the foreground copy.
 */
const TICKER_DEPARTURES = [
  "21:43  PARIS GARE DE LYON  →  VENEZIA S. LUCIA  ·  PLATFORM 14  ·  ON TIME",
  "06:55  AMSTERDAM CENTRAAL  →  BERLIN HBF  ·  ICE 145  ·  BOARDING",
  "18:12  BARCELONA SANTS  →  ZURICH HB  ·  TGV 9740  ·  ON TIME",
  "22:30  WIEN HBF  →  ROMA TERMINI  ·  NIGHTJET 233  ·  DEPARTED",
  "07:20  EDINBURGH WAVERLEY  →  LONDON KING'S X  ·  LNER 1S04  ·  ON TIME",
  "16:48  PRAHA HL.N.  →  BUDAPEST KELETI  ·  EC 173  ·  DELAYED 4'",
  "09:11  LISBOA ORIENTE  →  MADRID CHAMARTIN  ·  AVE 03240  ·  ON TIME",
  "14:02  KØBENHAVN H  →  HAMBURG HBF  ·  EC 35  ·  ON TIME",
];

export default function LiveAmbience({ variant = "hero", showTicker = true, embers = 18 }) {
  const [now, setNow] = useState("");

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const pad = (n) => n.toString().padStart(2, "0");
      setNow(`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Pre-randomise ember positions so they don't re-shuffle on re-render
  const [emberSeeds] = useState(() =>
    Array.from({ length: embers }).map(() => ({
      left: Math.random() * 100,
      delay: Math.random() * 14,
      duration: 10 + Math.random() * 12,
      size: 1 + Math.random() * 2,
      drift: -20 + Math.random() * 40,
    }))
  );

  // Duplicate the ticker content for seamless loop
  const tickerStream = [...TICKER_DEPARTURES, ...TICKER_DEPARTURES, ...TICKER_DEPARTURES];

  return (
    <>
      {/* Embers — gold dust rising from below */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="ambience-embers">
        {emberSeeds.map((e, i) => (
          <span
            key={i}
            className="absolute bottom-[-10px] block rounded-full bg-gold/60 animate-ember-rise"
            style={{
              left: `${e.left}%`,
              width: `${e.size}px`,
              height: `${e.size}px`,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.duration}s`,
              transform: `translateX(${e.drift}px)`,
              filter: "blur(0.5px)",
              boxShadow: "0 0 8px rgba(184,144,74,0.6)",
            }}
          />
        ))}
      </div>

      {/* Vertical scan-line — like a station departure board refresh sweep */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="ambience-scanline">
        <div
          className="absolute left-0 right-0 h-[120px] animate-scan-down"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(184,144,74,0.06) 50%, transparent 100%)",
          }}
        />
      </div>

      {/* Live status badge (top-right of hero) */}
      {variant === "hero" && (
        <div
          className="absolute top-6 right-6 hidden md:flex items-center gap-2 px-3 py-1.5 bg-ink/70 backdrop-blur-sm border border-panel-border rounded-sm text-[10px] font-display tracking-widest text-cream-dim"
          data-testid="ambience-live-clock"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
          </span>
          LIVE · TIMETABLES SYNCED · {now}
        </div>
      )}

      {/* Departure-board ticker — bottom edge */}
      {showTicker && (
        <div
          className="absolute bottom-0 left-0 right-0 overflow-hidden bg-ink/90 backdrop-blur-sm border-t border-gold/20 pointer-events-none"
          data-testid="ambience-ticker"
        >
          <div className="flex items-center whitespace-nowrap py-2 animate-ticker">
            {tickerStream.map((t, i) => (
              <span
                key={i}
                className="inline-flex items-center px-6 font-display text-[11px] tracking-[0.25em] text-gold/80"
              >
                <span className="h-1 w-1 rounded-full bg-gold mr-3" />
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
